import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthenticatedUser } from '@hygge/shared';
import { announceIdentityChange, api } from '@/lib/api';
import { AUTH_STORAGE_KEY, leaveForIdentitySwitch } from '@/lib/identity-switch';

interface AuthState {
  user: AuthenticatedUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  isImpersonating: boolean;

  // Actions
  setUser: (user: AuthenticatedUser | null) => void;
  setLoading: (loading: boolean) => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  initialize: () => Promise<void>;
  startImpersonation: (employeeId: string) => Promise<void>;
  exitImpersonation: () => Promise<void>;
}

// Promise deduplication to prevent multiple simultaneous API calls
let initializePromise: Promise<void> | null = null;
// Held while a Login as is in flight or the page is reloading after it. [HW-872]
let switchingIdentity = false;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      isInitialized: false,
      isImpersonating: false,

      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
          isLoading: false,
          isInitialized: true,
        }),

      setLoading: (isLoading) => set({ isLoading }),

      logout: async () => {
        try {
          // Call backend to clear cookies
          await api.post('/auth/logout');
        } catch {
          // Ignore errors, still clear local state
        }

        // Reset all auth state including isInitialized
        // This ensures next page load will re-check auth
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          isInitialized: true, // Keep initialized to prevent re-check loop
          isImpersonating: false,
        });
      },

      checkAuth: async () => {
        try {
          // Cookies are sent automatically with withCredentials: true
          const response = await api.get('/auth/me');
          set({
            user: response.data,
            isAuthenticated: true,
            isLoading: false,
            isImpersonating: !!response.data.isImpersonated,
          });
          return true;
        } catch {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
          return false;
        }
      },

      initialize: async () => {
        // Already initialized, skip
        if (get().isInitialized) return;

        // If initialization is in progress, wait for it
        if (initializePromise) {
          await initializePromise;
          return;
        }

        // Start initialization
        set({ isLoading: true });

        initializePromise = get().checkAuth().then(() => {
          set({ isInitialized: true });
          initializePromise = null;
        });

        await initializePromise;
      },

      // A switch of identity reloads the page. Every other in-memory store — the
      // TanStack Query cache, the extensions list — still holds what the previous
      // identity was shown, so after an SPA navigation the new person's name sat
      // over the old person's data. A full navigation drops all of it, including
      // stores added later, and doing it here means no call site can forget it.
      // `set` writes `auth-storage` to localStorage synchronously, so the new
      // user is persisted before the page goes away. `leaveForIdentitySwitch`
      // replaces the location and keeps unsaved-changes prompts from holding the
      // page here; a page Back restores from the browser's cache is reloaded by
      // `reloadPagesRestoredFromCache`. [HW-872]
      startImpersonation: async (employeeId: string) => {
        // One Login as at a time (the banner holds Exit to one the same way): a
        // second click would be refused ("already impersonating") once the first
        // has set the cookie, and its toast would flash while the page reloads.
        // The guard stays held once the page is leaving; only a refusal releases
        // it.
        if (switchingIdentity) return;
        switchingIdentity = true;
        try {
          const response = await api.post('/auth/impersonate', { employeeId });
          announceIdentityChange();
          set({
            user: response.data,
            isAuthenticated: true,
            isImpersonating: true,
          });
        } catch (err) {
          switchingIdentity = false;
          throw err;
        }
        leaveForIdentitySwitch('/');
      },

      exitImpersonation: async () => {
        const response = await api.post('/auth/exit-impersonation');
        announceIdentityChange();
        set({
          user: response.data,
          isAuthenticated: true,
          isImpersonating: false,
        });
        leaveForIdentitySwitch('/people');
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      partialize: (state) => ({
        // Only persist user data, not auth state
        // Auth state is verified via cookies on each page load
        user: state.user,
      }),
    }
  )
);
